import { useState } from 'react';
import { Button, Field, Input, Text, Tooltip, makeStyles } from '@fluentui/react-components';
import { AddCircle16Regular, AddCircleRegular, Dismiss16Regular, PhoneAddRegular } from '@fluentui/react-icons';
import { Phone, useInitialValues } from '../../data/types';
import { useValidation } from '../../data/types/validation';
import { Column, Row } from '../common';
import { DropdownMenu } from './Menus/DropdownMenu';
import { LinkButton } from './LinkButton';

const MAX_PHONES = 3;

const phoneTypes: string[] = ['Office', 'Cell', 'Other'];

const useStyles = makeStyles({
    row: {
        paddingBottom: '0.5rem',
    },
    phoneType: {
        minWidth: '10rem',
        width: '10rem',
    },
});

export type PhonesPanelProps = {
    phones?: Phone[];
    onChangePhones?: (phones: Phone[]) => void;
    onValidChange?: (isValid: boolean) => void;
};
export const PhonesPanel = (props: PhonesPanelProps) => {
    const { phones, onChangePhones, onValidChange } = props;

    //Hooks
    const initValues = useInitialValues();
    const { validatePhone } = useValidation();

    //State
    const [thePhones, setThePhones] = useState<Phone[]>(phones?.length ? phones : [initValues.phone()]);
    const [touched, setTouched] = useState<Record<number, boolean>>({});

    //Styles
    const styles = useStyles();

    //Logic
    const phoneTypeOptions = [...phoneTypes]
        .sort((a, b) => a.localeCompare(b))
        .map((phoneType) => ({
            label: phoneType,
            value: phoneType,
        }));

    const getNumberError = (number?: string) => {
        if (!number?.trim()) return undefined;
        try {
            validatePhone.schema.validateSyncAt('number', { number });
            return undefined;
        } catch (error) {
            return (error as Error).message;
        }
    };

    const applyPhones = (updated: Phone[]) => {
        setThePhones(updated);
        //Only entries with a valid number are published, and the first of those is the primary
        const completed = updated
            .filter((p) => !!p.number?.trim() && !getNumberError(p.number))
            .map((p, index) => ({ ...p, primary: index === 0 }));
        onChangePhones && onChangePhones(completed);
        onValidChange && onValidChange(updated.every((p) => !getNumberError(p.number)));
    };

    //UI Events
    const onChangeNumber = (index: number, number: string) => {
        applyPhones(thePhones.map((p, i) => (i === index ? { ...p, number } : p)));
    };
    const onBlurNumber = (index: number) => {
        setTouched((prev) => ({ ...prev, [index]: true }));
    };
    const onChangeType = (index: number, type: string) => {
        applyPhones(thePhones.map((p, i) => (i === index ? { ...p, type } : p)));
    };
    const onAddPhone = () => {
        setThePhones((prev) => (prev.length < MAX_PHONES ? [...prev, initValues.phone()] : prev));
    };
    const onRemovePhone = (index: number) => {
        applyPhones(thePhones.filter((_, i) => i !== index));
    };

    return (
        <>
            <Column gap='small'>
                <Text>Phone Numbers</Text>
                {thePhones.map((phoneObj, index) => (
                    <Row key={index} gap='medium' className={styles.row}>
                        <Column minWidth='9rem'>
                            <Field size='medium'>
                                <DropdownMenu
                                    className={styles.phoneType}
                                    size='medium'
                                    options={phoneTypeOptions}
                                    initialValue={phoneObj.type}
                                    onOptionChanged={(option) => onChangeType(index, option)}
                                    placeholder='Select Type'
                                    dataTestId={`phone-panel-type-combobox-${index}`}
                                />
                            </Field>
                        </Column>
                        <Column width='auto' maxWidth='25rem'>
                            <Field
                                size='medium'
                                validationState={touched[index] && getNumberError(phoneObj.number) ? 'error' : 'none'}
                                validationMessage={touched[index] ? getNumberError(phoneObj.number) : undefined}
                            >
                                <Input
                                    value={phoneObj.number}
                                    maxLength={validatePhone.number.maxLength}
                                    placeholder='Enter Phone Number'
                                    onChange={(_, data) => onChangeNumber(index, data.value)}
                                    onBlur={() => onBlurNumber(index)}
                                    data-testid={`phone-panel-number-input-${index}`}
                                />
                            </Field>
                        </Column>
                        <Column width='auto' minWidth='2rem' vAlign='top'>
                            {index > 0 && (
                                <Tooltip content='Remove Phone' relationship='description'>
                                    <Button
                                        appearance='transparent'
                                        icon={<Dismiss16Regular />}
                                        onClick={() => onRemovePhone(index)}
                                        data-testid={`phone-panel-remove-button-${index}`}
                                    />
                                </Tooltip>
                            )}
                        </Column>
                    </Row>
                ))}
                {thePhones.length < MAX_PHONES && (
                    <Row>
                        <Column width='auto'>
                            <Tooltip content='Add Phone' relationship='description'>
                                <LinkButton
                                    text='Add Phone'
                                    icon={<AddCircle16Regular />}
                                    onClick={onAddPhone}
                                    data-testid='phone-panel-add-button'
                                />
                            </Tooltip>
                            
                        </Column>
                    </Row>
                )}
            </Column>
        </>
    );
};
